import { useEffect, useRef } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { setCookie, getCookie, deleteCookie } from '@functions/cookie'
import type { LogoutOptions, RedirectLoginOptions } from '@auth0/auth0-react'

import {
  Box, Heading, Image, LoadingBubbles, Link, Text,
} from '@domain-group/suit-theme'

import LogoMarkUrl from './assets/logo-mark.svg'

type RenderLoginLinkParams = {
  error?: Error,
  loginWithRedirect: (options?: RedirectLoginOptions | undefined) => Promise<void>,
  logout: (options?: LogoutOptions | undefined) => void,
}

const renderLoginLink = (params: RenderLoginLinkParams) => {
  const { error, loginWithRedirect, logout } = params
  if (error){
    return <Link onClick={() => logout({ returnTo: window.location.origin })}>Try Another Account</Link>
  }

  return (
    <Link onClick={() => loginWithRedirect({ application: process.env.REACT_APP_APPLICATION_NAME })}>
      Login
    </Link>
  )
}

const getAuthenticatedUser = (user?: { email?: string }) => {
  const authenticatedUser = getCookie('authenticatedUser')
  if (authenticatedUser) return authenticatedUser

  return user?.email ?? null
}

const Login = () => {
  const authPayload = useAuth0()
  const {
    error, isAuthenticated, isLoading, user, loginWithRedirect, logout,
  } = authPayload

  const urlLogin = getCookie('urlLogin')
  const hasUsername = window.location.search.includes('username')

  if (!urlLogin && hasUsername) {
    setCookie('urlLogin', window.location.search, 86400) // 1 day
  }

  // use current search if it has username, otherwise fall back to cookie
  const params = hasUsername
    ? window.location.search
    : (urlLogin || '')

  const urlParams = new URLSearchParams(params)

  const newUser = hasUsername || urlLogin
    ? urlParams.get('username')
    : ''

  const authenticatedUser = getAuthenticatedUser(user)

  const logged = useRef(false)

  const loginFlowId = useRef(Math.random().toString()).current
  sessionStorage.setItem('loginFlowId', loginFlowId)

  useEffect(() => {
    if (!logged.current) {
      const logAttemptCreated = getCookie('logAttemptCreated')
      if (logAttemptCreated !== 'true') {
        logged.current = true
        setCookie('logAttemptCreated', 'true', 86400)
        window.newrelic?.addPageAction('Login Attempt', {
          realhubUsername: newUser,
          loginAttemptUser: newUser,
          currentEmail: user?.email,
          currentUserSub: user?.sub,
          loginFlowId,
          path: window.location.pathname,
          userLoggedOutCookie: String(getCookie('userLoggedOut')),
          logCreatedCookie: String(getCookie('logCreated')),
          logAttemptCreatedCookie: String(getCookie('logAttemptCreated'))
        })
      }
    }

    if (!error && !isLoading && isAuthenticated){
      if (isAuthenticated && newUser && authenticatedUser && newUser !== authenticatedUser){
        setCookie('mismatch', true, 86400) // 1 day
        setCookie('lastAuthenticatedUser', authenticatedUser, 86400) // 1 day
        setCookie('lastEmail', user?.email, 86400) // 1 day
        setCookie('lastUserSub', user?.sub, 86400) // 1 day
        setCookie('userLoggedOut', 'true', 86400)

        deleteCookie('authenticatedUser')
        deleteCookie('logCreated')

        logout({
          returnTo: window.location.origin,
          federated: true,
        })
      return
      }

      deleteCookie('logCreated')
    }

    if (!error && !isAuthenticated && !isLoading && newUser !== authenticatedUser){
      loginWithRedirect({
        application: process.env.REACT_APP_APPLICATION_NAME,
        appState: {
          userName: newUser,
          redirectPath: window.location.hash,
        },
      })
    }
  }, [error, isAuthenticated, isLoading, loginWithRedirect, logout, newUser, authenticatedUser])

  return (
    <Box
      alignItems="center"
      backgroundColor="whiteGrey"
      height="100vh"
      justifyContent="center"
      padding={['large', 'xxlarge']}
      width="100%"
    >
      <Box alignItems="center" flexDirection="column" justifyContent="center" width={['100%', '400px']}>
        <Image backgroundSize="contain" url={LogoMarkUrl} width={50} height={50} />

        <Heading textProps={{ color: 'bodyFontLightColor', marginTop: 'xlarge' }}>
          {error ? 'Error' : 'Loading Application...'}
        </Heading>

        {!error && <LoadingBubbles color="rgba(0, 0, 0, 0.15)" style={{ marginTop: 32 }} />}

        {error && <Text marginTop="xlarge">{error.message}</Text>}

        <Box alignItems="center" fontSize="xsmall" justifyContent="center" marginTop="xxlarge">
          <Text color="bodyFontLightColor" marginRight="small">Not Loading?</Text>
          {renderLoginLink({ error, loginWithRedirect, logout })}
        </Box>
      </Box>
    </Box>
  )
}

export default Login
